import React, { FC, useEffect } from "react";

// Components
import { TreatmentsCardList } from "../../FormElements/CardList/TreatmentsCardList";
import { InformationCardWithFilterDoctors } from "../../InformationCard/InformationCardWithFilterDoctors";
import { StepLayout } from "../StepLayout/StepLayout";
import { LocationDropdown } from "../../FormElements/Dropdown/LocationDropdown/LocationDropdown";

// Helpers
import { GtmService } from "../../GtmService";
import { setStorage } from "../../Helpers/formFunctions";
import { doctorInitialState } from "../../Helpers/initialState/doctorInitialState";
import { parentTreatmentInitialState } from "../../Helpers/initialState/parentTreatmentInitialState";
import { RootDataType } from "../../Helpers/interfaces";
import {
  findClosestParentContainerWithSpecializationName,
  findTreatmentById,
  isServiceContainer,
  isTreatmentContainer,
} from "../../Helpers/treatmentsHelpers";

// Types & Models
import { ServiceContainerDto } from "@apiModels/ServiceContainerDto.csharp";
import { TreatmentsContainerDto } from "@apiModels/TreatmentsContainerDto.csharp";
import {
  FormDispatch,
  FormFields,
  FormReducer,
  FormStateType,
} from "../../Helpers/formTypes";

// Hooks
import { useForm } from "react-hook-form";
import { useBookingContext } from "../../../BookingButton/BookingContext/BookingContext";
import { useFilteredLocationsForTreatment } from "../../Hooks/useFilteredLocationsForTreatment";
import { useFilteredTreatment } from "../../Hooks/useFilteredTreatment";
import { useLabels } from "../../Hooks/useLabels";
import { useLocationDropdown } from "../../Hooks/useLocationAndDoctorSelection";
import classNames from "classnames";

export interface TreatmentStepType {
  state: FormStateType;
  dispatch: FormDispatch;
  nextStep: () => void;
  rootData: RootDataType;
  handleGoBackButtonClick: () => void;
}

export const TreatmentStep: FC<TreatmentStepType> = ({
  state,
  dispatch,
  rootData,
  nextStep,
  handleGoBackButtonClick,
}) => {
  const { bookingLabels } = useLabels();
  const { newBookingFilterValues } = useBookingContext();

  const { filteredLocations, unavailableLocations } =
    useFilteredLocationsForTreatment(
      rootData.centers,
      state.treatment,
      state.doctor
    );
  const { setValue } = useForm<FormStateType>({
    defaultValues: state,
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  useEffect(() => {
    setStorage(state);
  }, [state]);

  const commonLabels = bookingLabels!.commonLabels;
  const { onLocationSelect, onDoctorSelect } = useLocationDropdown(
    filteredLocations,
    state,
    dispatch,
    setValue
  );

  const filteredTreatment = useFilteredTreatment(
    state.treatment as TreatmentsContainerDto,
    state.location,
    state.doctor,
    !!newBookingFilterValues?.doctorId
  );

  useEffect(() => {
    if (!filteredTreatment) return;

    // If there is only one treatment and it's a sub treatment, then select it
    if (filteredTreatment.items.length === 1 && !state.cameFromBackNavigation) {
      const treatment = filteredTreatment.items[0];
      onTreatmentSelect(treatment);
      return;
    }

    if (filteredTreatment.parentContainerId) {
      const parentTreatment = findTreatmentById(
        rootData.treatments,
        filteredTreatment.parentContainerId
      );
      dispatch({
        type: FormReducer.update,
        field: FormFields.parentTreatment,
        payload: parentTreatment,
      });
    } else {
      dispatch({
        type: FormReducer.update,
        field: FormFields.parentTreatment,
        payload: parentTreatmentInitialState,
      });
    }
  }, [filteredTreatment, rootData.treatments, state.cameFromBackNavigation]);

  const onTreatmentSelectByUser = (
    treatment: TreatmentsContainerDto | ServiceContainerDto
  ) => {
    dispatch({
      type: FormReducer.update,
      field: FormFields.cameFromBackNavigation,
      payload: false,
    });
    onTreatmentSelect(treatment);
  };
  const onTreatmentSelect = (
    treatment: TreatmentsContainerDto | ServiceContainerDto
  ) => {
    dispatch({
      type: FormReducer.update,
      field: FormFields.cameFromBackNavigation,
      payload: false,
    });
    const rootTreatment =
      findTreatmentById(rootData.treatments, treatment.containerId) ||
      treatment;
    if (isTreatmentContainer(rootTreatment)) {
      const treatmentContainer = rootTreatment;
      GtmService.treatmentStepSubmit(
        state.treatment.containerId,
        treatmentContainer.containerId,
        state.specializationName,
        state.service.specializationId,
        state.service.bookingName,
        state.service.id,
        state.location.name,
        state.location.id,
        state.doctor.name,
        state.doctor.id
      );
      dispatch({
        type: FormReducer.update,
        field: FormFields.treatment,
        payload: treatmentContainer,
      });
    } else if (isServiceContainer(rootTreatment)) {
      const serviceContainer = rootTreatment;
      const specializationName =
        findClosestParentContainerWithSpecializationName(
          serviceContainer,
          rootData.treatments
        );

      GtmService.treatmentStepSubmit(
        state.treatment.containerId,
        serviceContainer.containerId,
        state.specializationName,
        serviceContainer.specializationId,
        serviceContainer.bookingName,
        serviceContainer.id,
        state.location.name,
        state.location.id,
        state.doctor.name,
        state.doctor.id
      );

      dispatch({
        type: FormReducer.update,
        field: FormFields.service,
        payload: serviceContainer,
      });
      dispatch({
        type: FormReducer.update,
        field: FormFields.treatment,
        payload: state.treatment,
      });
      dispatch({
        type: FormReducer.update,
        field: FormFields.parentTreatment,
        payload: state.treatment,
      });
      dispatch({
        type: FormReducer.update,
        field: FormFields.specializationName,
        payload: specializationName,
      });

      setStorage(state);
      nextStep();
    }
  };

  const isDoctorSelected = state.doctor.id && state.doctor.id != "-1";

  const handleResetDoctorOnly = () => {
    if (isDoctorSelected) {
      dispatch({
        type: FormReducer.update,
        field: FormFields.doctor,
        payload: doctorInitialState,
      });
      setStorage({
        ...state,
        doctor: doctorInitialState,
      });
    }
  };

  return (
    <div className={classNames("TreatmentGroupStep")}>
      <div
        style={{
          margin: "1rem auto",
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <>
          <h1>Timebestilling</h1>
          <LocationDropdown
            defaultValue={state.location}
            label={commonLabels.placeDropdownLabel}
            placeholder={commonLabels.placeDropdownPlaceholder}
            onSelect={onLocationSelect}
            locations={{ items: filteredLocations }}
            unavailableLocations={{ items: unavailableLocations }}
            doctor={state.doctor}
            onDoctorSelect={onDoctorSelect}
            service={state.service}
            additionalLabels={commonLabels}
          />
        </>
      </div>
      <StepLayout>
        {isDoctorSelected && (
          <>
            <InformationCardWithFilterDoctors
              doctor={state.doctor}
              onResetFilters={handleResetDoctorOnly}
            />
          </>
        )}
        <div className="TreatmentGroupStep__selectContainer">
          <TreatmentsCardList
            treatment={filteredTreatment}
            onSelect={onTreatmentSelectByUser}
          />
        </div>
      </StepLayout>
    </div>
  );
};
